import {useCallback} from 'react'
import {
  type AppBskyActorDefs,
  moderateProfile,
  type ModerationOpts,
} from '@atproto/api'
import {keepPreviousData, useQuery, useQueryClient} from '@tanstack/react-query'

import {IS_CLEAR_COMMUNITY_HANDLE} from '#/lib/constants'
import {isJustAMute, moduiContainsHideableOffense} from '#/lib/moderation'
import {logger} from '#/logger'
import {STALE} from '#/state/queries'
import {
  matchesQuery,
  useLocalAccountsQuery,
} from '#/state/queries/community-local-accounts'
import {useAgent} from '#/state/session'
import {useModerationOpts} from '../preferences/moderation-opts'
import {DEFAULT_LOGGED_OUT_PREFERENCES} from './preferences'

const DEFAULT_MOD_OPTS = {
  userDid: undefined,
  prefs: DEFAULT_LOGGED_OUT_PREFERENCES.moderationPrefs,
}

const RQKEY_ROOT = 'actor-autocomplete'
export const RQKEY = (prefix: string) => [RQKEY_ROOT, prefix]

export function useActorAutocompleteQuery(
  prefix: string,
  maintainData?: boolean,
  limit?: number,
) {
  const moderationOpts = useModerationOpts()
  const agent = useAgent()
  const localAccounts = useLocalAccountsQuery()

  prefix = prefix.toLowerCase().trim()
  if (prefix.endsWith('.')) {
    // Going from "foo" to "foo." should not clear matches.
    prefix = prefix.slice(0, -1)
  }

  return useQuery<AppBskyActorDefs.ProfileViewBasic[]>({
    staleTime: STALE.MINUTES.ONE,
    queryKey: RQKEY(prefix || ''),
    async queryFn() {
      const res = prefix
        ? await agent.searchActorsTypeahead({
            q: prefix,
            limit: limit || 8,
          })
        : undefined
      return res?.data.actors || []
    },
    select: useCallback(
      (data: AppBskyActorDefs.ProfileViewBasic[]) => {
        const localMatches = (localAccounts.data ?? []).filter(actor =>
          matchesQuery(actor, prefix),
        )
        return computeSuggestions({
          q: prefix,
          searched: [...localMatches, ...data],
          moderationOpts: moderationOpts || DEFAULT_MOD_OPTS,
        })
      },
      [prefix, moderationOpts, localAccounts.data],
    ),
    placeholderData: maintainData ? keepPreviousData : undefined,
  })
}

export type ActorAutocompleteFn = ReturnType<typeof useActorAutocompleteFn>
export function useActorAutocompleteFn() {
  const queryClient = useQueryClient()
  const moderationOpts = useModerationOpts()
  const agent = useAgent()
  const localAccounts = useLocalAccountsQuery()

  return useCallback(
    async ({query, limit = 8}: {query: string; limit?: number}) => {
      query = query.toLowerCase()
      let res
      if (query) {
        try {
          res = await queryClient.fetchQuery({
            staleTime: STALE.MINUTES.ONE,
            queryKey: RQKEY(query || ''),
            queryFn: () =>
              agent.searchActorsTypeahead({
                q: query,
                limit,
              }),
          })
        } catch (e) {
          logger.error('useActorSearch: searchActorsTypeahead failed', {
            message: e,
          })
        }
      }

      const localMatches = (localAccounts.data ?? []).filter(actor =>
        matchesQuery(actor, query),
      )

      return computeSuggestions({
        q: query,
        searched: [...localMatches, ...(res?.data.actors ?? [])],
        moderationOpts: moderationOpts || DEFAULT_MOD_OPTS,
      })
    },
    [queryClient, moderationOpts, agent, localAccounts.data],
  )
}

function computeSuggestions({
  q,
  searched = [],
  moderationOpts,
}: {
  q?: string
  searched?: AppBskyActorDefs.ProfileViewBasic[]
  moderationOpts: ModerationOpts
}) {
  let items: AppBskyActorDefs.ProfileViewBasic[] = []
  for (const item of searched) {
    if (!items.find(item2 => item2.handle === item.handle)) {
      items.push(item)
    }
  }
  return items
    .filter(profile => {
      const modui = moderateProfile(profile, moderationOpts).ui('profileList')
      const isExactMatch = q && profile.handle.toLowerCase() === q
      return (
        (isExactMatch && !moduiContainsHideableOffense(modui)) ||
        !modui.filter ||
        isJustAMute(modui)
      )
    })
    .sort(
      (a, b) =>
        Number(IS_CLEAR_COMMUNITY_HANDLE(b.handle)) -
        Number(IS_CLEAR_COMMUNITY_HANDLE(a.handle)),
    )
}
